import React from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { useBreakdownColorMap } from 'features/charts-v2/context/breakdown-color-map-context'
import { type MetricType } from 'features/charts-v2/types'
import {
  calculateXAxisHeightFromLabels,
  formatAxisNumber,
} from 'features/charts-v2/utils/axis-tick-utils'
import { categoryRow } from 'features/charts-v2/utils/build-hover-annotation'
import { getMetricsLabel } from 'features/charts-v2/utils/metric-format-utils'

interface StackedAreaDataItem {
  name: string
  [key: string]: string | number | undefined
}

interface StackedAreaChartProps {
  data: StackedAreaDataItem[]
  breakdownCategories: string[]
  selectedMetrics: MetricType[]
  hoveredDataPoint?: string | null
  onDataPointHover?: (dataPoint: string | null) => void
  onSegmentClick?: (
    segmentData: {
      primaryBreakdown: string
      stackingBreakdown?: string
      value: number
      payload: StackedAreaDataItem
    },
    event: React.MouseEvent,
  ) => void
  fontSizePx?: number
  showXAxisLabel?: boolean
  showYAxisLabel?: boolean
  showDatapointLabels?: boolean
  xAxisOverflow?: 'expand' | 'wrap' | 'truncate'
  yAxisOverflow?: 'expand' | 'wrap' | 'truncate'
  disableAnimation?: boolean
}

/**
 * Format number for display in labels
 * - Whole numbers: no decimals (e.g., 5)
 * - Decimals: max 2 decimal places (e.g., 4.91, 3.5)
 */
const formatLabelValue = (value: number): string => {
  if (Number.isInteger(value)) {
    return value.toString()
  }
  // Round to 2 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(2)).toString()
}

/**
 * Custom label component for stacked area total
 * Displays the total value at each x-axis point
 */
const CustomStackedAreaLabel = React.memo<{
  x?: number
  y?: number
  value?: number
  payload?: StackedAreaDataItem
  categories?: string[]
  fontSizePx?: number
}>(({ x = 0, y = 0, payload, categories = [], fontSizePx = 13 }) => {
  // Calculate total from all categories
  if (!payload || !categories || categories.length === 0) return null

  const total = categories.reduce((sum, category) => {
    return sum + ((payload[category] as number) || 0)
  }, 0)

  // Don't render label if total is 0
  if (total === 0) return null

  return (
    <text
      x={x}
      y={y - 10}
      fill="#333"
      fontSize={fontSizePx}
      textAnchor="middle"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {formatLabelValue(total)}
    </text>
  )
})

CustomStackedAreaLabel.displayName = 'CustomStackedAreaLabel'

/**
 * Color palette for breakdown categories
 */
const BREAKDOWN_COLORS = [
  '#3b82f6', // Blue
  '#10b981', // Green
  '#f59e0b', // Yellow
  '#ef4444', // Red
  '#8b5cf6', // Purple
  '#06b6d4', // Cyan
  '#ec4899', // Pink
  '#84cc16', // Lime
  '#f97316', // Orange
  '#6366f1', // Indigo
  '#14b8a6', // Teal
  '#a855f7', // Violet
]

/**
 * StackedAreaChart component for displaying breakdown categories as stacked areas
 * Works with any breakdown categories and time buckets
 */
export const StackedAreaChart: React.FC<StackedAreaChartProps> = ({
  data,
  breakdownCategories,
  selectedMetrics,
  hoveredDataPoint,
  onDataPointHover,
  onSegmentClick,
  fontSizePx = 13,
  showXAxisLabel = true,
  showYAxisLabel = true,
  showDatapointLabels = true,
  xAxisOverflow = 'expand',
  disableAnimation = false,
}) => {
  const colorMapProp = useBreakdownColorMap()

  // Extract labels from chart data
  const labels = data.map((d) => String(d.name ?? ''))

  // Calculate x-axis height dynamically based on overflow mode
  let xAxisHeight: number
  let bottomMargin: number

  if (xAxisOverflow === 'expand') {
    // Calculate based on actual text width for angled labels
    xAxisHeight = calculateXAxisHeightFromLabels(labels, fontSizePx)
    bottomMargin = 20
  } else {
    // Use fixed values for wrap and truncate modes
    xAxisHeight = xAxisOverflow === 'wrap' ? 120 : 80
    bottomMargin = xAxisOverflow === 'wrap' ? 30 : 20
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: bottomMargin,
        }}
        onMouseMove={(chartData: { activeLabel?: string }) => {
          if (chartData && chartData.activeLabel) {
            onDataPointHover?.(chartData.activeLabel)
          }
        }}
        onMouseLeave={() => onDataPointHover?.(null)}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          angle={-45}
          textAnchor="end"
          height={xAxisHeight}
          interval={0}
          tick={{ fontSize: fontSizePx }}
          label={
            showXAxisLabel
              ? {
                  value: '',
                  position: 'insideBottom',
                  offset: -10,
                  style: {
                    textAnchor: 'middle',
                    fill: '#666',
                    fontSize: fontSizePx,
                  },
                }
              : undefined
          }
        />
        <YAxis
          tick={{ fontSize: fontSizePx }}
          tickFormatter={formatAxisNumber}
          label={
            showYAxisLabel
              ? {
                  value: getMetricsLabel(selectedMetrics),
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    textAnchor: 'middle',
                    fill: '#666',
                    fontSize: fontSizePx,
                  },
                }
              : undefined
          }
        />

        <Tooltip
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload
              const total = breakdownCategories.reduce(
                (sum, category) => sum + (data[category] || 0),
                0,
              )

              const rows = breakdownCategories.map((category, index) => {
                const value = data[category] || 0
                const percentage =
                  total > 0 ? ((value / total) * 100).toFixed(1) : '0'
                return categoryRow(
                  category,
                  value.toLocaleString(),
                  `${percentage}%`,
                  colorMapProp.size > 0
                    ? (colorMapProp.get(category) ?? null)
                    : (BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length] ??
                        null),
                )
              })

              return (
                <ChartDatapointHoverAnnotation
                  title={String(label)}
                  rows={rows}
                  footer={{
                    label: 'Total',
                    value: total.toLocaleString(),
                    percentage: null,
                    color: null,
                  }}
                  focusedField={null}
                />
              )
            }
            return null
          }}
        />

        {/* Dynamic Areas for each breakdown category */}
        {breakdownCategories.map((category, index) => {
          const isLastCategory = index === breakdownCategories.length - 1
          const areaColor =
            (colorMapProp.size > 0 ? colorMapProp.get(category) : undefined) ??
            BREAKDOWN_COLORS[index % BREAKDOWN_COLORS.length]
          return (
            <Area
              key={category}
              type="monotone"
              dataKey={category}
              stackId="breakdown"
              stroke={areaColor}
              fill={areaColor}
              fillOpacity={hoveredDataPoint ? 0.4 : 0.7}
              strokeWidth={1}
              name={category}
              isAnimationActive={!showDatapointLabels && !disableAnimation}
              label={
                showDatapointLabels && isLastCategory
                  ? (props: Record<string, unknown>) => (
                      <CustomStackedAreaLabel
                        {...props}
                        categories={breakdownCategories}
                        fontSizePx={fontSizePx}
                      />
                    )
                  : false
              }
              onClick={
                ((
                  data: { payload?: StackedAreaDataItem },
                  _index: number,
                  event: React.MouseEvent,
                ) => {
                  if (onSegmentClick && data && data.payload) {
                    onSegmentClick(
                      {
                        primaryBreakdown: data.payload.name,
                        stackingBreakdown: category,
                        value: (data.payload[category] as number) || 0,
                        payload: data.payload,
                      },
                      event,
                    )
                  }
                }) as never
              }
            />
          )
        })}
      </AreaChart>
    </ResponsiveContainer>
  )
}
